import React, { useState, useEffect } from "react";
import { useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Card,
  Col,
  ColorPicker,
  Divider,
  Row,
  Select,
  Space,
  Button,
  Alert,
  theme,
  Typography,
  Segmented,
  message,
  Modal
} from "antd";
import { 
  SunOutlined, 
  MoonOutlined, 
  RocketOutlined, 
  BgColorsOutlined, 
  AppstoreOutlined,
  SettingOutlined 
} from "@ant-design/icons";
import tinycolor from "tinycolor2";

import {
  updateTheme,
  updateThemeTemplate, // ✅ นำ action นี้กลับมาใช้
  saveThemeRequest,
} from "@/AppRedux/actions/theme/index";

import { useParams } from "react-router-dom";
import { RootState } from "@/AppRedux/reducers";
import type { ThemeData } from "@/AppRedux/actions/theme/themeType";

import ThemeLivePreview from "./ThemeLivePreview";
import { themeOptions } from "@/AppRedux/reducers/theme/index"; // ✅ Import options กลับมา

const { useToken } = theme;
const { Title, Text } = Typography;

// ==============================================
// 🧠 SMART THEME GENERATOR LOGIC (Auto Contrast)
// ==============================================
const generateThemeFromColor = (baseColor: string, mode: 'default' | 'dark') => {
  const isDark = mode === 'dark';
  const color = tinycolor(baseColor);
  
  // 1. Setup Global Colors
  const colorPrimary = baseColor;
  const colorBgBase = isDark 
    ? '#000000' 
    : tinycolor(baseColor).lighten(50).saturate(10).toHexString(); 
  const colorTextBase = isDark ? '#ffffff' : '#2B2D42';

  // 2. Setup Layout & Header Colors
  const headerBg = colorPrimary;
  
  // 🔥 AUTO CONTRAST: เช็คว่า Header มืดหรือสว่าง เพื่อเลือกสีตัวอักษร
  const isHeaderLight = tinycolor(headerBg).isLight();
  const headerTextColor = isHeaderLight ? '#000000' : '#ffffff';

  // 3. Setup Menu Colors
  const menuItemColor = headerTextColor; 
  
  // Menu Active Logic
  let menuItemSelectedColor;
  if (!isHeaderLight) {
     menuItemSelectedColor = '#FFD23F'; // สีทอง ตัดกับพื้นเข้ม
  } else {
     menuItemSelectedColor = tinycolor(colorPrimary).darken(30).toHexString();
  }

  return {
    algorithm: mode,
    token: {
      colorPrimary,
      colorInfo: colorPrimary,
      colorSuccess: "#06D6A0",
      colorWarning: "#FFB627",
      colorError: "#EF233C",
      colorBgBase,
      colorTextBase,
      borderRadius: 8,
      fontFamilyUrl: "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
      fontFamily: "'IBM Plex Sans Thai', 'IBM Plex Sans', sans-serif",
    },
    components: {
      Layout: {
        headerBg: headerBg,
        headerColor: headerTextColor,
        bodyBg: colorBgBase,
      },
      Button: { 
        borderRadius: 8,
        primaryShadow: `0 2px 0 ${tinycolor(colorPrimary).darken(10).toString()}`
      },
      Card: { borderRadius: 12 },
      Input: { borderRadius: 8 },
      Menu: {
        itemColor: menuItemColor,
        itemHoverColor: isHeaderLight 
            ? tinycolor(menuItemColor).lighten(20).toHexString() 
            : tinycolor(menuItemColor).setAlpha(0.8).toHexString(),
        itemSelectedColor: menuItemSelectedColor, 
        horizontalItemSelectedColor: menuItemSelectedColor,
        horizontalItemHoverColor: menuItemSelectedColor,
        colorBgElevated: headerBg, 
        groupTitleColor: tinycolor(headerTextColor).setAlpha(0.7).toHexString(),
      },
      Select: {
        colorText: colorTextBase,
        colorBgElevated: isDark ? '#1f1f1f' : '#ffffff',
        optionSelectedColor: colorPrimary,
        optionSelectedBg: tinycolor(colorPrimary).setAlpha(0.1).toHexString(),
      },
    },
  };
};

const ThemeSettings = () => {
  const dispatch = useDispatch();
  const { propcode = "default" } = useParams<{ propcode: string }>();
  const { token } = useToken();
  const { loading, error, data } = useSelector((state: RootState) => state.theme);

  // ✅ State สำหรับจัดการ Mode การแก้ไข (Smart / Template / Manual)
  const [editMode, setEditMode] = useState<'smart' | 'template' | 'manual'>('smart');
  
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [brandColor, setBrandColor] = useState<string>("#FF6B35");
  const [mode, setMode] = useState<'default' | 'dark'>('default');
  const initialThemeRef = useRef<ThemeData | null>(null);
  const isDirtyRef = useRef(false);

  const markDirty = () => {
    isDirtyRef.current = true;
  };

  // Sync state from Redux
  useEffect(() => {
    if (data?.token?.colorPrimary) {
      setBrandColor(data.token.colorPrimary);
    }
    if (data?.algorithm) {
      const algos = Array.isArray(data.algorithm) ? data.algorithm : [data.algorithm];
      setMode(algos.includes('dark') ? 'dark' : 'default');
    }
  }, [data]);

  // Capture initial (or last saved) theme for reset
  useEffect(() => {
    if (!isDirtyRef.current) {
      initialThemeRef.current = data;
    }
  }, [data]);

  // Reset theme when leaving without saving
  useEffect(() => {
    return () => {

      if (isDirtyRef.current && initialThemeRef.current) {
        dispatch(updateThemeTemplate(initialThemeRef.current));
      }
    };
  }, [dispatch]);

  // 1. Handler: Smart Mode (Auto Generate)
  const handleBrandColorChange = (color: any) => {
    markDirty();
    const hex = color.toHexString();
    setBrandColor(hex);
    const newTheme = generateThemeFromColor(hex, mode);
    dispatch(updateTheme({ ...data, ...newTheme }));
  };

  // 2. Handler: Template Selection (Load Preset)
  const handleTemplateChange = (value: string) => {
    setSelectedTemplate(value);
    const selectedTheme = themeOptions.find((t) => t.value === value)?.theme;
    
    if (selectedTheme) {
      markDirty();
      // Dispatch ธีมจาก Template
      dispatch(updateThemeTemplate(selectedTheme));
      
      // Update local states เพื่อให้ UI สอดคล้อง
      if (selectedTheme.token?.colorPrimary) {
          setBrandColor(selectedTheme.token.colorPrimary);
      }
      
      // Reset algorithm to default unless template specifies otherwise
      setMode('default'); 
    }
  };

  // 3. Handler: Light/Dark Toggle
  const handleModeChange = (newMode: 'default' | 'dark') => {
    markDirty();
    setMode(newMode);
    
    if (editMode === 'smart') {
        // ถ้าอยู่ Smart Mode ให้ Gen ใหม่โดยใช้สีเดิม
        const newTheme = generateThemeFromColor(brandColor, newMode);
        dispatch(updateTheme({ ...data, ...newTheme }));
    } else {
        // ถ้าเป็น Template/Manual ให้เปลี่ยนแค่ Algorithm และ Background
        const newToken = { ...data.token };
        if (newMode === 'dark') {
            newToken.colorBgBase = '#000000';
            newToken.colorTextBase = '#ffffff';
        } else {
            newToken.colorBgBase = '#ffffff';
            newToken.colorTextBase = '#000000';
        }
        dispatch(updateTheme({
            ...data,
            algorithm: newMode === 'dark' ? 'dark' : 'default',
            token: newToken
        }));
    }
  };

  const handleSave = () => {
    isDirtyRef.current = false;
    initialThemeRef.current = data;
    dispatch(saveThemeRequest(propcode, data));
    //notification.success({ message: "Theme Saved", description: "Theme updated successfully." });
     setTimeout(() => {
      message.success('Changes saved successfully!');
      Modal.info({
        title: 'Saved JSON Data',
        width: 800,
        content: (
          <pre style={{ maxHeight: 400, overflow: 'auto', background: '#f5f5f5', border: '1px solid #ddd', padding: 10 }}>
            {JSON.stringify({
              ...data,
            }, null, 2)}
          </pre>
        ),
        onOk() { },
      });
    }, 1000);
  };

  // Manual Handlers
  const handleColorChange = (color: any, key: string) => {
    markDirty();
    dispatch(updateTheme({ ...data, token: { ...data.token, [key]: color.toHexString() } }));
  };

  const handleComponentColorChange = (componentName: string, key: string, color: any) => {
    markDirty();
    // Clone components object เพื่อความปลอดภัยของ State
    const newComponents = { ...(data.components || {}) };
    const targetComponent = { ...(newComponents[componentName] || {}) };
    
    const hexColor = color.toHexString();

    // อัปเดตค่าสีตัวหลัก
    targetComponent[key] = hexColor;

    // ✅ [FIX] เพิ่ม Logic: ถ้าเป็น Menu ให้ Sync ค่าของ Horizontal ด้วย
    if (componentName === 'Menu') {
        if (key === 'itemSelectedColor') {
            targetComponent['horizontalItemSelectedColor'] = hexColor;
        }
        if (key === 'itemHoverColor') {
            targetComponent['horizontalItemHoverColor'] = hexColor;
        }
    }

    newComponents[componentName] = targetComponent;

    dispatch(
      updateTheme({
        ...data,
        components: newComponents,
      })
    );
  };

  // Render Helpers
  const renderColorItem = (label: string, tokenKey: string) => (
    <Col span={12} key={tokenKey}>
      <Space orientation="vertical" style={{ width: "100%" }}>
        <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
        <ColorPicker showText value={data?.token?.[tokenKey]} onChange={(c) => handleColorChange(c, tokenKey)} style={{ width: '100%' }} />
      </Space>
    </Col>
  );
  
  const renderComponentColorItem = (componentName: string, label: string, tokenKey: string) => (
    <Col span={12} key={`${componentName}-${tokenKey}`}>
      <Space orientation="vertical" style={{ width: "100%" }}>
        <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
        <ColorPicker showText allowClear value={data?.components?.[componentName]?.[tokenKey]} onChange={(c) => handleComponentColorChange(componentName, tokenKey, c)} style={{ width: '100%' }} />
      </Space>
    </Col>
  );

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[24, 24]}>
        {/* Sidebar Controls */}
        <Col span={24} lg={7} xl={6}>
          <Card
            title={<Title level={4} style={{ margin: 0 }}>Theme Editor</Title>}
            variant="borderless"
            style={{ 
                height: 'calc(100vh - 48px)', 
                overflowY: 'auto', 
                boxShadow: token.boxShadowTertiary,
                background: mode === 'dark' ? token.colorBgContainer : '#fff' 
            }}
          >
            <Space orientation="vertical" size="large" style={{ width: "100%" }}>

              {/* Theme Mode (Light/Dark) */}
              <div>
                <div style={{ marginBottom: 12, fontWeight: 500 }}>Display Mode</div>
                <Segmented
                  block
                  options={[
                    { label: 'Light', value: 'default', icon: <SunOutlined /> },
                    { label: 'Dark', value: 'dark', icon: <MoonOutlined /> },
                  ]}
                  value={mode}
                  onChange={(val) => handleModeChange(val as 'default' | 'dark')}
                />
              </div>

               <Divider style={{ margin: '12px 0' }} />

               {/* ✅ Edit Mode Selector (Smart | Template | Manual) */}
               <div>
                   <div style={{ marginBottom: 12, fontWeight: 500 }}>Setup Method</div>
                   <Segmented 
                        block
                        value={editMode}
                        onChange={(val) => setEditMode(val as any)}
                        options={[
                            { label: 'Smart', value: 'smart', icon: <RocketOutlined /> },
                            { label: 'Presets', value: 'template', icon: <AppstoreOutlined /> },
                            { label: 'Manual', value: 'manual', icon: <SettingOutlined /> },
                        ]}
                   />
               </div>

               {/* 🚀 1. SMART MODE UI */}
               {editMode === 'smart' && (
                   <div style={{ 
                       padding: '20px', 
                       marginTop: 16,
                       background: mode === 'dark' ? 'rgba(255,255,255,0.05)' : token.colorFillAlter, 
                       borderRadius: 12, 
                       border: `1px dashed ${token.colorBorder}` 
                   }}>
                        <Text strong style={{display:'block', marginBottom: 8}}>Brand Color</Text>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                            Pick one color to auto-generate the entire theme including Header, Menu, and Layouts.
                        </Text>
                        <ColorPicker
                            showText
                            value={brandColor}
                            onChange={handleBrandColorChange}
                            style={{ width: '100%' }}
                            size="large"
                        />
                   </div>
               )}

               {/* 🎨 2. TEMPLATE MODE UI */}
               {editMode === 'template' && (
                   <div style={{ marginTop: 16 }}>
                       <Text strong style={{display:'block', marginBottom: 8}}>Select Template</Text>
                       <Select
                          style={{ width: "100%" }}
                          size="large"
                          value={selectedTemplate}
                          onChange={handleTemplateChange}
                          options={themeOptions.map((t) => ({
                            label: t.label,
                            value: t.value,
                          }))}
                        />
                        <Alert 
                            message="Template Selected" 
                            description="You can switch to 'Manual' mode to fine-tune specific colors after selecting a template." 
                            type="info" 
                            showIcon 
                            style={{ marginTop: 16, fontSize: 12 }} 
                        />
                   </div>
               )}

               {/* ⚙️ 3. MANUAL MODE UI (Existing) */}
               {editMode === 'manual' && (
                   <div style={{ marginTop: 16 }}>
                        <CollapseGhost title="Global Colors">
                            <Row gutter={[12, 16]}>
                                {renderColorItem("Primary", "colorPrimary")}
                                {renderColorItem("Background", "colorBgBase")}
                                {renderColorItem("Text", "colorTextBase")}
                            </Row>
                        </CollapseGhost>
                        <Divider style={{ margin: '12px 0' }} />
                        <CollapseGhost title="Layout & Header">
                            <Row gutter={[12, 16]}>
                                {renderComponentColorItem("Layout", "Header BG", "headerBg")}
                                {renderComponentColorItem("Layout", "Header Text", "headerColor")}
                                {renderComponentColorItem("Layout", "Body BG", "bodyBg")}
                            </Row>
                        </CollapseGhost>
                        <Divider style={{ margin: '12px 0' }} />
                        <CollapseGhost title="Menu">
                            <Row gutter={[12, 16]}>
                                {renderComponentColorItem("Menu", "Item Text", "itemColor")}
                                {renderComponentColorItem("Menu", "Selected BG", "itemSelectedBg")}
                                {renderComponentColorItem("Menu", "Selected Text", "itemSelectedColor")}
                            </Row>
                        </CollapseGhost>
                   </div>
               )}

              <Divider style={{ margin: '12px 0' }} />

              <Button
                type="primary"
                size="large"
                onClick={handleSave}
                style={{ width: "100%", height: 48, fontWeight: 600, boxShadow: `0 4px 12px ${token.colorPrimary}40` }}
                loading={loading}
                icon={<BgColorsOutlined />}
              >
                Save Theme
              </Button>
            </Space>
          </Card>
        </Col>

        {/* Live Preview Area */}
        <Col span={24} lg={17} xl={18}>
          <ThemeLivePreview themeConfig={data} />
        </Col>
      </Row>
    </div>
  );
};

// Helper Component
const CollapseGhost = ({ title, children }: any) => (
    <div>
        <div style={{ marginBottom: 12, fontWeight: 600, color: '#888' }}>{title}</div>
        {children}
    </div>
);

export default ThemeSettings;
